import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Clock, Lock, Phone, Hourglass, Building2, Tag, Flame } from "lucide-react";
import { DrumIcon, HutIcon, BaobabIcon } from "@/components/AfricanIcons";
import SpeakButton from "@/components/SpeakButton";

const MONTHS_EN = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const DOW_EN = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const TYPE_ICON = { workshop: DrumIcon, training: BaobabIcon, side_event: HutIcon };

const LANG_LABEL = { en: "EN", fr: "FR", ar: "AR", pt: "PT" };

export default function SessionCard({ session, onReserve }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || "en";
  const title = session.title?.[lng] || session.title?.en || "";
  const remaining = session.capacity - session.reserved_count;
  const isClosed = !!session.closed;
  const isTbc = !!session.time_tbc;
  const status = isClosed ? "closed" : isTbc ? "tbc" : (remaining <= 0 ? "full" : remaining <= 5 ? "limited" : "available");

  const startTime = new Date(session.starts_at);
  const endTime = new Date(session.ends_at);
  const fmt = (d) => d.toLocaleTimeString(lng, { hour: "numeric", minute: "2-digit", hour12: true });

  const isAmber = session.type === "side_event";
  const Icon = TYPE_ICON[session.type] || HutIcon;

  const seatPillClass =
    status === "full" ? "seats-pill-rose" :
    status === "closed" ? "seats-pill-rose" :
    isAmber ? "seats-pill-amber" : "";

  const btnDisabled = isClosed || isTbc || status === "full";
  const btnLabel =
    isClosed ? "Closed · Invitation only" :
    isTbc ? "Time TBC · Notify me" :
    status === "full" ? t("session.full") :
    t("session.reserve");

  const pct = Math.min(100, Math.round((session.reserved_count / Math.max(1, session.capacity)) * 100));
  const barClass = status === "full" ? "is-full" : (remaining <= 5 ? "is-warn" : "");

  return (
    <div className="diplomatic-card p-4 sm:p-5" data-accent={isAmber ? "amber" : "forest"} data-testid={`session-card-${session.id}`}>
      <div className="flex items-start gap-3 sm:gap-4">
        {/* Date block */}
        <div className={`date-block ${isAmber ? "date-block-amber" : ""}`}>
          <div className="day-lbl">{DOW_EN[startTime.getDay()]}</div>
          <div className="day-num">{String(startTime.getDate()).padStart(2, "0")}</div>
          <div className="month-lbl">{MONTHS_EN[startTime.getMonth()]}</div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[9px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${isAmber ? "bg-[color:var(--amber-soft)] text-[color:var(--amber-dark)]" : "bg-[color:var(--forest-soft)] text-[color:var(--forest-dark)]"}`}>
              {t(`filters.${session.type}`)}
            </span>
            {session.native_language && (
              <span data-testid={`session-lang-${session.id}`} className="text-[9px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-white border border-[color:var(--line-strong)] text-[color:var(--ink-2)]">
                {LANG_LABEL[session.native_language] || session.native_language}
              </span>
            )}
            {isClosed && (
              <span data-testid={`badge-closed-${session.id}`} className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
                <Lock className="w-2.5 h-2.5" /> Closed
              </span>
            )}
            {isTbc && (
              <span data-testid={`badge-tbc-${session.id}`} className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                <Hourglass className="w-2.5 h-2.5" /> Time TBC
              </span>
            )}
            {!isClosed && !isTbc && remaining > 0 && remaining <= 5 && (
              <span data-testid={`badge-seats-nudge-${session.id}`} className="seats-pulse inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[color:var(--amber)] text-white shadow">
                <Flame className="w-2.5 h-2.5" /> Only {remaining} seats left
              </span>
            )}
          </div>
          <div className="flex items-start gap-2">
            <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isAmber ? "text-[color:var(--amber)]" : "text-[color:var(--forest)]"}`} />
            <Link
              to={`/session/${session.id}`}
              data-testid={`session-title-link-${session.id}`}
              className="flex-1 group"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-bold text-[color:var(--ink)] text-base sm:text-lg leading-snug group-hover:text-[color:var(--forest)] transition-colors">
                {title}
              </h3>
            </Link>
            <SpeakButton
              testId={`speak-${session.id}`}
              text={`${title}. ${session.focal_person ? `Focal person ${session.focal_person}. ` : ""}${isTbc ? "Time to be confirmed" : `Starting at ${fmt(startTime)}`} at ${session.room}.`}
              label=""
              className="!p-1.5 flex-shrink-0"
            />
          </div>
          <div className="mt-2 space-y-1 text-sm text-[color:var(--ink-2)]">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 opacity-70" />
              {isTbc
                ? <span className="italic">Time TBC · ~2h duration</span>
                : <span>{fmt(startTime)} – {fmt(endTime)}</span>}
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 opacity-70" />
              <a
                data-testid={`session-map-${session.id}`}
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${session.room} Dakar Senegal`)}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="truncate underline decoration-dotted underline-offset-2 hover:text-[color:var(--forest)] transition-colors inline-flex items-center gap-1"
                title="Open in Google Maps"
              >
                <span className="truncate">{session.room}</span>
                <span className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--ink-3)]">Map</span>
              </a>
            </div>
            {session.organization && (
              <div className="flex items-center gap-1.5" data-testid={`session-organization-${session.id}`}>
                <Building2 className="w-3.5 h-3.5 opacity-70" />
                <span className="truncate font-semibold text-[color:var(--forest-dark)]">{session.organization}</span>
              </div>
            )}
            {session.topic && (
              <div className="flex items-center gap-1.5" data-testid={`session-topic-${session.id}`}>
                <Tag className="w-3.5 h-3.5 opacity-70" />
                <span className="truncate italic">{session.topic}</span>
              </div>
            )}
            {session.focal_person && (
              <div className="flex items-start gap-1.5" data-testid={`session-focal-${session.id}`}>
                <Phone className="w-3.5 h-3.5 opacity-70 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="truncate">
                    <span className="font-semibold">{session.focal_person}</span>
                    {session.focal_contact ? <> · <span className="font-mono text-[11px]">{session.focal_contact}</span></> : null}
                  </div>
                  {session.focal_person_2 && (
                    <div className="truncate" data-testid={`session-focal2-${session.id}`}>
                      <span className="font-semibold">{session.focal_person_2}</span>
                      {session.focal_contact_2 ? <> · <span className="font-mono text-[11px]">{session.focal_contact_2}</span></> : null}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="mt-2.5">
            <span className={`seats-pill ${seatPillClass}`}>
              {isClosed
                ? `Capacity ${session.capacity} · invitation only`
                : `${session.reserved_count} / ${session.capacity} seats ${status === "full" ? "· full" : "left"}`}
            </span>
          </div>
        </div>
      </div>

      {/* Capacity progress */}
      {!isClosed && !isTbc && (
        <div className="mt-3 capacity-bar" data-testid={`capacity-bar-${session.id}`} aria-hidden="true">
          <div className={`capacity-bar-fill ${barClass}`} style={{ width: `${pct}%` }} />
        </div>
      )}

      <div className="mt-4">
        <button
          data-testid={`reserve-btn-${session.id}`}
          onClick={() => !btnDisabled && onReserve(session)}
          disabled={btnDisabled}
          className={`btn-reserve ${isAmber ? "btn-reserve-amber" : "btn-reserve-green"} inline-flex items-center justify-center gap-2`}
        >
          {isClosed && <Lock className="w-4 h-4" />}
          {isTbc && <Hourglass className="w-4 h-4" />}
          {btnLabel}
        </button>
      </div>
    </div>
  );
}
